/**
 * IXVerify v2 — composite exception-based review (ixv2 WP-3.2; plan §3.2 step 3).
 *
 * All green = a glance: study, visit, dispense date, subject (+initials sanity
 * check), DOB in large type, per-kit ticks — nothing to tap per field. Only
 * exceptions expand for interaction:
 *   - amber fields demand a correcting tap (accept or edit — the recorded
 *     human decision, R5; corrections ride the confirm call server-side);
 *   - an unknown subject opens the inline-add sheet;
 *   - kit discrepancies offer rescan / recorded decisions / record-mismatch.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronLeft,
  GraduationCap,
  Loader2,
  Pencil,
  ShieldAlert,
  UserPlus,
  XCircle,
} from 'lucide-react';
import type { IxvExtraction, IxvKit, IxvSubject, KitDecision, SessionDetail } from '../../lib/api';
import {
  canConfirm,
  kitProgress,
  kitsNeedingDecision,
  listExceptions,
  REVIEW_FIELDS,
  type ReviewFieldName,
} from '../../lib/captureMachine';

const FIELD_LABEL: Record<ReviewFieldName, string> = {
  protocol_id: 'Study / protocol',
  subject_number: 'Subject',
  visit_name: 'Visit',
  dispense_date: 'Dispense date',
};

const fieldInputCls =
  'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 motion-reduce:transition-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';

function KitStatusIcon({ kit }: { kit: IxvKit }) {
  if (kit.match_status === 'matched' || kit.match_status === 'manual_confirmed') {
    return <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" aria-hidden="true" />;
  }
  if (kit.match_status === 'pending') {
    return <Loader2 className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />;
  }
  if (kit.match_status === 'missing') {
    return <XCircle className="h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />;
  }
  return <ShieldAlert className="h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />;
}

const KIT_STATUS_TEXT: Record<string, string> = {
  matched: 'Matched',
  manual_confirmed: 'Confirmed by you',
  pending: 'Not scanned yet',
  mismatch: 'Mismatch',
  extra: 'Extra scan',
  missing: 'Marked missing',
};

export interface ReviewScreenProps {
  detail: SessionDetail;
  extraction: IxvExtraction | null;
  subject: IxvSubject | null;
  subjectMatch: 'matched' | 'unknown' | 'no_study' | null;
  teaching: boolean;
  /** Field names the CRC edited or accepted (R5 gating). */
  touched: ReadonlySet<string>;
  edits: Partial<Record<ReviewFieldName, string | null>>;
  onEditField: (field: ReviewFieldName, value: string) => void;
  onAcceptField: (field: ReviewFieldName | 'kits') => void;
  confirmed: boolean;
  confirming: boolean;
  onConfirm: () => void;
  onAddSubject: () => void;
  onKitDecision: (kitId: string, decision: KitDecision, reason: string) => void;
  onRescanKit: (kitId: string) => void;
  onBackToCapture: () => void;
  onProceed: () => void;
  onRecordMismatch: () => void;
  canProceed: boolean;
}

export default function ReviewScreen(props: ReviewScreenProps) {
  const {
    detail, extraction, subject, subjectMatch, teaching, touched, edits,
    onEditField, onAcceptField, confirmed, confirming, onConfirm, onAddSubject,
    onKitDecision, onRescanKit, onBackToCapture, onProceed, onRecordMismatch, canProceed,
  } = props;

  const [editing, setEditing] = useState<ReviewFieldName | null>(null);
  const [decisionFor, setDecisionFor] = useState<{ kit: IxvKit; decision: KitDecision } | null>(null);
  const [decisionReason, setDecisionReason] = useState('');

  const exceptions = listExceptions(extraction);
  const exceptionFields = new Set(exceptions.map((e) => e.field));
  const progress = kitProgress(detail.kit_state);
  const discrepancies = kitsNeedingDecision(detail.kits);
  const confirmReady = canConfirm(extraction, touched);

  const fieldValue = (f: ReviewFieldName): string =>
    (f in edits ? edits[f] : extraction?.fields?.[f]?.value) ?? '';

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-gray-50 dark:bg-gray-900" data-testid="review-screen">
      <div className="mx-auto w-full max-w-lg px-4 py-4 pb-28">
        {teaching && (
          <div className="mb-3 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20" role="status">
            <GraduationCap className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <p className="text-sm text-primary-800 dark:text-primary-300">
              <span className="font-semibold">Teaching IXVerify.</span> First time seeing this study — your review here is
              the teaching. Nothing extra to do.
            </p>
          </div>
        )}

        {/* Assignment fields — exception-based */}
        <section aria-label="Assignment" className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Assignment</h2>
          <dl className="space-y-2">
            {REVIEW_FIELDS.map((f) => {
              const isException = exceptionFields.has(f);
              const isTouched = touched.has(f);
              const value = fieldValue(f);
              return (
                <div key={f} className={isException ? 'rounded-lg border border-amber-300 bg-amber-50 p-2.5 dark:border-amber-700 dark:bg-amber-900/20' : 'flex items-center justify-between gap-2'}>
                  {!isException ? (
                    <>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">{FIELD_LABEL[f]}</dt>
                      <dd className="flex items-center gap-1.5 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                        {value || <span className="text-gray-400">—</span>}
                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                      </dd>
                    </>
                  ) : (
                    <div data-testid={`amber-${f}`}>
                      <div className="flex items-center justify-between gap-2">
                        <dt className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
                          <AlertTriangle className="h-4 w-4" aria-hidden="true" /> {FIELD_LABEL[f]}
                        </dt>
                        {isTouched && <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />}
                      </div>
                      {editing === f ? (
                        <div className="mt-1.5 flex gap-2">
                          <input
                            aria-label={FIELD_LABEL[f]}
                            className={fieldInputCls}
                            value={value}
                            autoFocus
                            onChange={(e) => onEditField(f, e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setEditing(null)}
                            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
                          >
                            Done
                          </button>
                        </div>
                      ) : (
                        <>
                          <dd className="mt-0.5 text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                            {value || <span className="text-gray-400">not read</span>}
                          </dd>
                          <div className="mt-1.5 flex gap-2">
                            <button
                              type="button"
                              onClick={() => onAcceptField(f)}
                              disabled={isTouched}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-amber-400 px-2.5 py-1.5 text-xs font-medium text-amber-800 transition-colors duration-150 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400 disabled:opacity-50 motion-reduce:transition-none dark:text-amber-300 dark:hover:bg-amber-900/40 sm:min-h-0"
                            >
                              <Check className="h-3.5 w-3.5" aria-hidden="true" /> {isTouched ? 'Accepted' : 'Looks right'}
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditing(f)}
                              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                            >
                              <Pencil className="h-3.5 w-3.5" aria-hidden="true" /> Correct
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </dl>

          {/* Confirm CTA (records corrections server-side — the learning signal) */}
          {!confirmed && extraction && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={!confirmReady || confirming}
              className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
              data-testid="confirm-assignment"
            >
              {confirming ? <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}
              {exceptions.length === 0 ? 'Confirm assignment' : confirmReady ? 'Confirm with corrections' : 'Review the amber fields first'}
            </button>
          )}
          {confirmed && (
            <p className="mt-3 flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> Assignment confirmed
            </p>
          )}
        </section>

        {/* Subject + DOB */}
        <section aria-label="Subject" className="mt-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Subject</h2>
          {subject ? (
            <div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">{subject.subject_number}</span>
                {subject.initials && (
                  <span className="text-sm text-gray-500 dark:text-gray-400">Initials {subject.initials}</span>
                )}
              </div>
              {/* DOB in large type (§3.2 — unmissable) */}
              <p className="mt-2 text-2xl font-semibold tabular-nums text-gray-900 dark:text-gray-100" data-testid="review-dob">
                {subject.dob || <span className="text-base font-normal text-amber-700 dark:text-amber-400">DOB unavailable</span>}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Date of birth — confirm verbally with the patient</p>
            </div>
          ) : subjectMatch === 'no_study' ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Confirm the assignment first to resolve the study and subject.</p>
          ) : (
            <div data-testid="subject-unknown">
              <p className="flex items-center gap-1.5 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                {extraction?.fields?.subject_number?.value
                  ? `Subject ${extraction.fields.subject_number.value} is not on the roster yet.`
                  : 'No subject matched.'}
              </p>
              <button
                type="button"
                onClick={onAddSubject}
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                <UserPlus className="h-4 w-4" aria-hidden="true" /> Add subject
              </button>
            </div>
          )}
        </section>

        {/* Kits */}
        <section aria-label="Kits" className="mt-3 rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Kits</h2>
            {progress.assigned > 0 && (
              <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
                {progress.resolved}/{progress.assigned} verified
              </span>
            )}
          </div>
          {detail.kits.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No kits yet — confirm the assignment, then scan each kit.</p>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {detail.kits.map((kit) => (
                <li key={kit.id} className="py-2" data-testid={`kit-${kit.match_status}`}>
                  <div className="flex items-center gap-2">
                    <KitStatusIcon kit={kit} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium tabular-nums text-gray-900 dark:text-gray-100">
                        {kit.assigned_kit_number || kit.scanned_kit_number}
                        {kit.assigned_kit_number && kit.scanned_kit_number && kit.assigned_kit_number !== kit.scanned_kit_number && (
                          <span className="text-red-700 dark:text-red-400"> ≠ {kit.scanned_kit_number}</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {KIT_STATUS_TEXT[kit.match_status]}
                        {kit.scan_method === 'manual' && ' · manual entry'}
                      </p>
                      {kit.ambiguity && (
                        <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                          Easily-confused characters — assigned reads <span className="font-medium tabular-nums">{kit.ambiguity.assigned_reading}</span>,
                          scan reads <span className="font-medium tabular-nums">{kit.ambiguity.scanned_reading}</span>. Your call.
                        </p>
                      )}
                    </div>
                  </div>
                  {['mismatch', 'extra'].includes(kit.match_status) && (
                    <div className="mt-1.5 flex flex-wrap gap-2 pl-7">
                      <button
                        type="button"
                        onClick={() => onRescanKit(kit.id)}
                        className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Rescan
                      </button>
                      {kit.ambiguity && (
                        <button
                          type="button"
                          onClick={() => { setDecisionFor({ kit, decision: 'confirm_match' }); setDecisionReason(''); }}
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          They match
                        </button>
                      )}
                      {kit.scanned_kit_number && (
                        <button
                          type="button"
                          onClick={() => { setDecisionFor({ kit, decision: 'remove' }); setDecisionReason(''); }}
                          className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                        >
                          Remove scan
                        </button>
                      )}
                    </div>
                  )}
                  {kit.match_status === 'pending' && !kit.scanned_kit_number && kit.assigned_kit_number && (
                    <div className="mt-1.5 pl-7">
                      <button
                        type="button"
                        onClick={() => { setDecisionFor({ kit, decision: 'mark_missing' }); setDecisionReason(''); }}
                        className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                      >
                        Kit not present
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Mismatch banner (§3.2 — unmissable, names the kits) */}
        {discrepancies.length > 0 && (
          <div className="mt-3 rounded-xl border border-red-300 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20" role="alert" data-testid="mismatch-banner">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-800 dark:text-red-300">
              <ShieldAlert className="h-5 w-5" aria-hidden="true" />
              Kit discrepancy — do not dispense yet
            </p>
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">
              Rescan to fix a bad read, or record the mismatch. A mismatch record never produces a dispense-verified report.
            </p>
            <button
              type="button"
              onClick={onRecordMismatch}
              className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 motion-reduce:transition-none sm:min-h-0"
              data-testid="record-mismatch"
            >
              Record mismatch
            </button>
          </div>
        )}
      </div>

      {/* Sticky footer nav */}
      <div
        className="fixed inset-x-0 bottom-0 z-10 border-t border-gray-200 bg-white/95 px-4 py-2 backdrop-blur dark:border-gray-700 dark:bg-gray-900/95"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
      >
        <div className="mx-auto flex w-full max-w-lg items-center justify-between gap-2">
          <button
            type="button"
            onClick={onBackToCapture}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 motion-reduce:transition-none dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Camera
          </button>
          <button
            type="button"
            onClick={onProceed}
            disabled={!canProceed}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
            data-testid="proceed-attest"
          >
            Attest &amp; publish
          </button>
        </div>
      </div>

      {/* Kit decision sheet (reason required — recorded human decision, R5) */}
      {decisionFor && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {decisionFor.decision === 'confirm_match' && 'Confirm this kit matches'}
              {decisionFor.decision === 'mark_missing' && 'Mark kit as not present'}
              {decisionFor.decision === 'remove' && 'Remove this scan'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {decisionFor.kit.assigned_kit_number || decisionFor.kit.scanned_kit_number} — a reason is recorded with your decision.
            </p>
            <textarea
              aria-label="Reason"
              className={`${fieldInputCls} mt-3`}
              rows={2}
              placeholder="Reason"
              value={decisionReason}
              onChange={(e) => setDecisionReason(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDecisionFor(null)}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!decisionReason.trim()}
                onClick={() => {
                  onKitDecision(decisionFor.kit.id, decisionFor.decision, decisionReason.trim());
                  setDecisionFor(null);
                }}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                data-testid="kit-decision-confirm"
              >
                Record decision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
